// What a search phrase says about the home itself rather than the place:
// "pet friendly", "avec garage", "1500 sq ft", "new listings". Each part is
// taken out of the phrase and mapped to a filter that already exists, so it can
// never end up as a city name (smart search, 2026-09-29).
//
// Letters are matched with \p{L}: \b does not see the edge of a word that ends
// in "é" or starts with "à".

export interface FoundFeatures {
  petFriendly?: true;
  furnished?: 'furnished' | 'unfurnished';
  garageSpaces?: number;
  /** Amenity ids of AMENITIES: "pool", "balcony", "waterfront"... */
  amenities?: string[];
  /** Living area in square feet; square metres are converted. */
  minArea?: number;
  tag?: 'new_listings' | 'price_reduced';
}

const WORD = (words: string) => new RegExp(String.raw`(?<![\p{L}\d])(?:${words})(?![\p{L}\d])`, 'giu');
const WITH = String.raw`(?:(?:with|avec)\s+)?`;

const PETS = WORD(String.raw`pets?[ -]?(?:friendly|allowed|ok|welcome)|animaux? (?:accept[ée]s?|permis|admis|bienvenus?)|acceptent? les animaux`);
const UNFURNISHED = WORD(String.raw`unfurnished|non[ -]meubl[ée]e?s?|sans meubles`);
const FURNISHED = WORD(String.raw`furnished|meubl[ée]e?s?`);
const GARAGE = WORD(`${WITH}(?:an? )?garages?`);

const AMENITY_WORDS: Array<{ id: string; words: RegExp }> = [
  { id: 'pool', words: WORD(`${WITH}(?:swimming pool|pool|piscine)`) },
  { id: 'gym', words: WORD(`${WITH}(?:gym|fitness|salle de sport|salle d'entra[iî]nement)`) },
  { id: 'balcony', words: WORD(`${WITH}(?:balcony|balcon)`) },
  { id: 'fireplace', words: WORD(`${WITH}(?:fireplace|foyer|chemin[ée]e)`) },
  { id: 'waterfront', words: WORD(String.raw`water ?front|lake ?front|river ?front|(?:au )?bord de l'eau`) },
  { id: 'rooftop', words: WORD(String.raw`rooftop|roof terrace|toit-terrasse|terrasse (?:sur le |au )?toit`) },
  { id: 'ev_charging', words: WORD(String.raw`ev charger|ev charging|electric car charger|borne de recharge`) },
  { id: 'smart_home', words: WORD(String.raw`smart home|maison intelligente|domotique`) },
];

// A bare "new" is a place too (New Westminster, Nouveau-Brunswick): it counts
// as a tag only when a kind of home follows it.
const KIND = String.raw`(?=\s+(?:listings?|homes?|properties|condos?|houses?|apartments?|townhouses?|maisons?|appartements?|annonces?|logements?|propri[ée]t[ée]s?)(?![\p{L}\d]))`;
const NEW_WORDS = WORD(String.raw`new listings?|newly listed|just listed|nouvelles? (?:annonces?|inscriptions?)|nouveaut[ée]s?|(?:new|nouveaux?|nouvelles?)${KIND}`);
const REDUCED_WORDS = WORD(String.raw`price reduced|reduced price|price drops?|price cuts?|prix r[ée]duits?|baisses? de prix`);

const NUMBER = String.raw`(\d{1,3}(?:[ , ]\d{3})+|\d+(?:[.,]\d+)?)`;
const SQFT = new RegExp(String.raw`${NUMBER}\s*\+?\s*(?:sq\.? ?ft\.?|sqft|square feet|square foot|pi²|pi2|pieds? carr[ée]s?|sf)(?![\p{L}\d])`, 'iu');
const SQM = new RegExp(String.raw`${NUMBER}\s*\+?\s*(?:m²|m2|sqm|square met(?:er|re)s?|m[eè]tres? carr[ée]s?)(?![\p{L}\d])`, 'iu');

const FEET_PER_METRE_SQUARED = 10.7639;

/** An area, taken out first so "1500 sq ft" is never read as a price. */
export function takeArea(text: string): { rest: string; minArea?: number } {
  const feet = text.match(SQFT);
  const metres = feet ? null : text.match(SQM);
  const found = feet || metres;
  if (!found) return { rest: text };
  const n = parseFloat(found[1].replace(/[ , ]/g, '').replace(',', '.'));
  const sqft = metres ? n * FEET_PER_METRE_SQUARED : n;
  const rest = text.replace(found[0], ' ');
  return Number.isFinite(sqft) && sqft >= 100 && sqft <= 100_000 ? { rest, minArea: Math.round(sqft) } : { rest };
}

/** The features named in the phrase, and the phrase without them. */
export function takeFeatures(text: string): { rest: string; found: FoundFeatures } {
  let rest = text;
  const found: FoundFeatures = {};
  const take = (re: RegExp): boolean => {
    re.lastIndex = 0;
    if (!re.test(rest)) return false;
    re.lastIndex = 0;
    rest = rest.replace(re, ' ');
    return true;
  };
  if (take(PETS)) found.petFriendly = true;
  if (take(UNFURNISHED)) found.furnished = 'unfurnished';
  else if (take(FURNISHED)) found.furnished = 'furnished';
  if (take(GARAGE)) found.garageSpaces = 1;
  const amenities = AMENITY_WORDS.filter((a) => take(a.words)).map((a) => a.id);
  if (amenities.length) found.amenities = amenities;
  if (take(REDUCED_WORDS)) found.tag = 'price_reduced';
  else if (take(NEW_WORDS)) found.tag = 'new_listings';
  return { rest, found };
}

const AMENITY_LABELS: Record<string, { en: string; fr: string }> = {
  pool: { en: 'Pool', fr: 'Piscine' },
  gym: { en: 'Gym', fr: 'Salle de sport' },
  balcony: { en: 'Balcony', fr: 'Balcon' },
  fireplace: { en: 'Fireplace', fr: 'Foyer' },
  waterfront: { en: 'Waterfront', fr: "Bord de l'eau" },
  rooftop: { en: 'Rooftop', fr: 'Terrasse sur le toit' },
  ev_charging: { en: 'EV charger', fr: 'Borne de recharge' },
  smart_home: { en: 'Smart home', fr: 'Maison intelligente' },
};

/** One chip per feature the phrase named; "drop" lists the fields the x takes off. */
export function featureChips(n: FoundFeatures, fr: boolean): { key: string; drop: string[]; label: string }[] {
  const t = (en: string, frText: string) => (fr ? frText : en);
  const chips: { key: string; drop: string[]; label: string }[] = [];
  if (n.petFriendly) chips.push({ key: 'pets', drop: ['petFriendly'], label: t('Pets allowed', 'Animaux permis') });
  if (n.furnished) chips.push({ key: 'furnished', drop: ['furnished'], label: n.furnished === 'furnished' ? t('Furnished', 'Meublé') : t('Unfurnished', 'Non meublé') });
  if (n.garageSpaces) chips.push({ key: 'garage', drop: ['garageSpaces'], label: t('Garage', 'Garage') });
  for (const id of n.amenities ?? []) {
    const label = AMENITY_LABELS[id];
    if (label) chips.push({ key: `amenity-${id}`, drop: ['amenities'], label: fr ? label.fr : label.en });
  }
  if (n.minArea) chips.push({ key: 'area', drop: ['minArea'], label: fr ? `${n.minArea} pi² et plus` : `${n.minArea}+ sq ft` });
  if (n.tag) chips.push({ key: 'tag', drop: ['tag'], label: n.tag === 'new_listings' ? t('New this week', 'Nouveautés') : t('Price reduced', 'Prix réduit') });
  return chips;
}
